/**
 * AttachmentItem Component
 * 
 * Displays individual file attachments in the AttachModal with hover interactions.
 * Matches Figma design specifications with file type icons and remove functionality.
 * 
 * Features:
 * - File type icon display based on MIME type
 * - Hover state with remove (X) button
 * - File name display with truncation
 * - Accessible keyboard navigation
 * - Clean integration with chatStore
 */

import React, { useState } from 'react';
import { Box, Text, Tooltip, Loader, ActionIcon } from '@mantine/core';
import { IconCheck, IconAlertTriangle } from '@tabler/icons-react';
import { getFileTypeIcon, getFileTypeIconFromFilename, getRemoveIcon, formatFileName } from './utils';
import type { AttachmentItemProps, VisualState } from './types';
import classes from './AttachmentItem.module.css';

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Compute visual state based on staging state
 */
const getVisualState = (stagingState?: string): VisualState => {
  switch (stagingState) {
    case 'staged-for-upload':
      return {
        borderColor: 'blue',
        label: 'Ready to upload',
        strikethrough: false
      };
    case 'staged-for-delete':
      return {
        borderColor: 'red', 
        label: 'Will be deleted',
        strikethrough: true
      };
    case 'uploaded':
      return {
        borderColor: 'green',
        label: 'Uploaded',
        strikethrough: false
      };
    default:
      return {
        borderColor: 'normal',
        label: undefined,
        strikethrough: false
      };
  }
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const AttachmentItem: React.FC<AttachmentItemProps> = ({
  attachment,
  filename,
  readOnly = false,
  canDelete = false,
  isDeleting = false,
  stagingState = 'none',
  onRemove,
  className,
  style,
  uploadStatusOverride,
}) => {
  // =============================================================================
  // LOCAL STATE
  // =============================================================================
  
  const [isHovered, setIsHovered] = useState(false);

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================
  
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleRemoveClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    // Handle both attachment objects and filename strings
    const id = attachment ? attachment.id : `uploaded-${filename}`;
    onRemove(id);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Handle Enter and Space for accessibility
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      // Handle both attachment objects and filename strings
      const id = attachment ? attachment.id : `uploaded-${filename}`;
      onRemove(id);
    }
  };

  // =============================================================================
  // DERIVED STATE
  // =============================================================================
  
  // Handle both attachment objects and filename strings
  const fileData = attachment ? {
    id: attachment.id,
    name: attachment.file.name,
    file: attachment.file,
    uploadStatus: attachment.uploadStatus,
    error: attachment.error
  } : {
    id: `uploaded-${filename}`,
    name: filename!,
    file: null,
    uploadStatus: (uploadStatusOverride ?? 'completed') as 'pending' | 'uploading' | 'completed' | 'error',
    error: undefined
  };
  
  const FileIconComponent = fileData.file 
    ? getFileTypeIcon(fileData.file) 
    : getFileTypeIconFromFilename(fileData.name);
  const RemoveIconComponent = getRemoveIcon();
  const displayFileName = formatFileName(fileData.name);
  const fullFileName = fileData.name;
  
  // Visual state based on staging
  const visualState = getVisualState(stagingState);
  
  // Upload status logic
  const isUploading = fileData.uploadStatus === 'uploading';
  const isCompleted = fileData.uploadStatus === 'completed';
  const isError = fileData.uploadStatus === 'error';
  const isUploadedFile = !attachment && filename; // Previously uploaded file
  const isDisabled = isUploading || isDeleting || (isCompleted && !canDelete) || (readOnly && !canDelete);
  const showHoverEffects = !isDisabled && (canDelete || !isCompleted);
  
  // Status icon logic
  const getStatusIcon = () => {
    if (isUploading) {
      return <Loader size={16} color="blue" />;
    }
    if (isDeleting) {
      return <Loader size={16} color="red" />;
    }
    if (isCompleted) {
      return <IconCheck size={16} color="green" />;
    }
    if (isError) {
      return <IconAlertTriangle size={16} color="red" />;
    }
    return null;
  };

  // =============================================================================
  // RENDER
  // =============================================================================
  
  return (
    <Tooltip 
      label={fullFileName !== displayFileName ? fullFileName : undefined}
      position="top"
      disabled={fullFileName === displayFileName}
    >
      <Box
        className={`
          ${classes.attachmentItem} 
          ${className || ''} 
          ${isDisabled ? classes.disabled : ''} 
          ${stagingState === 'staged-for-upload' ? classes.stagedForUpload : ''} 
          ${stagingState === 'staged-for-delete' ? classes.stagedForDelete : ''} 
          ${stagingState === 'uploaded' ? classes.uploaded : ''}
        `.trim()}
        style={style}
        onMouseEnter={showHoverEffects ? handleMouseEnter : undefined}
        onMouseLeave={showHoverEffects ? handleMouseLeave : undefined}
        data-upload-status={fileData.uploadStatus}
      >
        {/* Icon Container - shows file icon, remove icon, or status icon */}
        <Box className={classes.iconContainer}>
          {/* Status Icon - shows when uploading/deleting/error, but hide check icon for uploaded state */}
          {(getStatusIcon() && (!isHovered || !canDelete || isDeleting || isUploading) && stagingState !== 'uploaded') && (
            <Box className={classes.statusIcon}>
              {getStatusIcon()}
            </Box>
          )}
          
          {/* File Type Icon - visible when no status and not hovering, or when in uploaded state, but hide when deleting */}
          {((!getStatusIcon() && !isHovered) || (stagingState === 'uploaded' && !isDeleting) || (stagingState === 'staged-for-delete' && !isDeleting)) && (
            <Box className={classes.fileIcon}>
              <FileIconComponent size={18} />
            </Box>
          )}
          
          {/* Remove Icon - visible on hover when deletable */}
          {showHoverEffects && (
            <Box className={classes.removeIcon}>
              <button
                type="button"
                className={classes.removeButton}
                onClick={handleRemoveClick}
                onKeyDown={handleKeyDown}
                aria-label={`Remove ${fileData.name}`}
              >
                <RemoveIconComponent size={18} />
              </button>
            </Box>
          )}
        </Box>

        {/* File Name with Status */}
        <Box className={classes.fileNameContainer}>
          <Text className={`${classes.fileName} ${isError ? classes.errorText : ''}`}>
            {displayFileName}
          </Text>
          {isUploading && (
            <Text className={classes.progressText} size="xs" c="dimmed">
              Uploading...
            </Text>
          )}
          {isDeleting && (
            <Text className={classes.progressText} size="xs" c="dimmed">
              Deleting...
            </Text>
          )}
          {isError && fileData.error && (
            <Text className={classes.errorText} size="xs" c="red">
              {fileData.error}
            </Text>
          )}
          {/* Show staging label only when not uploading or deleting */}
          {visualState.label && !isUploading && !isDeleting && (
            <Text 
              className={`${classes.stagingLabel} ${
                stagingState === 'staged-for-upload' ? 'upload' : 
                stagingState === 'staged-for-delete' ? 'delete' : 
                stagingState === 'uploaded' ? 'uploaded' : ''
              }`} 
              size="xs"
            >
              {visualState.label}
            </Text>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export default AttachmentItem;
